import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';

import {
    getIdentifiedFlagsState,
    subscribeIdentifiedFlags,
} from '@/analytics/posthog/identifiedFlags';

/**
 * Whether the flags PostHog holds are its answer for the identified user.
 *
 * The answer is followed from `identify` on (see `identifiedFlags`), so a page opened after it has
 * arrived trusts the flags at once, and one opened after a load has finished since identify trusts
 * the next load. While `waiting` and short of an answer, it asks PostHog to reload so that the
 * load it needs happens at all; PostHog folds a reload into the request already queued.
 */
export function useIdentifiedFlagAnswer(waiting: boolean): boolean {
    const posthog = usePostHog();
    // State set from the PostHog callback rather than useSyncExternalStore: posthog-js/react sets
    // the flag value with useState from that same callback, so both land in one render and the
    // gate never shows `disabled` for a frame before an identified `true`.
    const [state, setState] = useState(getIdentifiedFlagsState);

    useEffect(() => {
        const sync = (): void => setState(getIdentifiedFlagsState());
        const unsubscribe = subscribeIdentifiedFlags(sync);
        sync();
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (!waiting) return;
        if (state === 'awaitingLoad' || state === 'awaitingIdentifiedLoad') {
            posthog.reloadFeatureFlags();
        }
    }, [posthog, waiting, state]);

    return state === 'answered';
}
